import { appApi, idTag, orgTag } from '@/app/redux/appApi';
import { Assessment } from '@/common/types/assessment';
import {
  PlacementTest,
  PlacementTestResult,
} from '@/common/types/placementTest';
import api from '@/db/apiClient';

interface PlacementTestsData {
  placementTests: PlacementTest[];
  placementTestResults: PlacementTestResult[];
}

const assessmentsApi = appApi.injectEndpoints({
  endpoints: (build) => ({
    getAssessmentsForOrganisation: build.query<Assessment[], string>({
      query: (orgId) =>
        api.asEducator.getAssessmentsForOrganisation(orgId),
      providesTags: (result, _err, orgId) =>
        result
          ? [orgTag('Assessments', orgId), ...result.map((a) => idTag('Assessments', a.id))]
          : [orgTag('Assessments', orgId)],
    }),

    getPlacementTestsAndResultsForOrganisation: build.query<
      PlacementTestsData,
      string
    >({
      query: (orgId) =>
        api.asEducator.getPlacementTestsAndResultsForOrganisation(orgId),
      providesTags: (_result, _err, orgId) => [orgTag('PlacementTests', orgId)],
    }),

    createAssessment: build.mutation<Assessment, Omit<Assessment, 'id'>>({
      query: async (data) => {
        const id = await api.asEducator.createAssessment(data);
        return { id, ...data };
      },
      invalidatesTags: (_res, _err, data) => [orgTag('Assessments', data.organisationId)],
    }),

    updateAssessment: build.mutation<
      null,
      { orgId: string; assessmentId: string; updates: Partial<Assessment> }
    >({
      query: async ({ assessmentId, updates }) => {
        await api.asEducator.updateAssessment(assessmentId, updates);
        return null;
      },
      onQueryStarted: async (
        { orgId, assessmentId, updates },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          assessmentsApi.util.updateQueryData(
            'getAssessmentsForOrganisation',
            orgId,
            (draft) => {
              const a = draft.find((x) => x.id === assessmentId);
              if (a) Object.assign(a, updates, { updatedAt: Date.now() });
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    deleteAssessment: build.mutation<null, { orgId: string; assessmentId: string }>({
      query: async ({ assessmentId }) => {
        await api.asEducator.deleteAssessment(assessmentId);
        return null;
      },
      onQueryStarted: async (
        { orgId, assessmentId },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          assessmentsApi.util.updateQueryData(
            'getAssessmentsForOrganisation',
            orgId,
            (draft) => {
              const idx = draft.findIndex((a) => a.id === assessmentId);
              if (idx !== -1) draft.splice(idx, 1);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),

    deletePlacementTest: build.mutation<
      null,
      { orgId: string; placementTestId: string }
    >({
      query: async ({ placementTestId }) => {
        await api.asEducator.deletePlacementTest(placementTestId);
        return null;
      },
      onQueryStarted: async (
        { orgId, placementTestId },
        { dispatch, queryFulfilled },
      ) => {
        const patch = dispatch(
          assessmentsApi.util.updateQueryData(
            'getPlacementTestsAndResultsForOrganisation',
            orgId,
            (draft) => {
              const idx = draft.placementTests.findIndex(
                (t) => t.id === placementTestId,
              );
              if (idx !== -1) draft.placementTests.splice(idx, 1);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
    }),
  }),

  overrideExisting: false,
});

export default assessmentsApi;
export const {
  useGetAssessmentsForOrganisationQuery,
  useGetPlacementTestsAndResultsForOrganisationQuery,
  useCreateAssessmentMutation,
  useUpdateAssessmentMutation,
  useDeleteAssessmentMutation,
  useDeletePlacementTestMutation,
} = assessmentsApi;
